import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Cookie, ArrowLeft } from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';

export default function CookiePolicyPage() {
  const { theme } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="cookie-policy-page" className={`min-h-screen pt-24 ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Back link */}
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 mb-8 hover:text-[#00C8EB] transition-colors ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-xl bg-[#00C8EB]/10 flex items-center justify-center">
              <Cookie size={28} weight="duotone" className="text-[#00C8EB]" />
            </div>
            <div>
              <h1 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Cookie Policy
              </h1>
              <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Last updated: March 2026
              </p>
            </div>
          </div>

          {/* Content */}
          <div className={`prose prose-lg max-w-none ${theme === 'dark' ? 'prose-invert' : ''}`}>
            <div className={`space-y-8 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              
              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  1. Introduction
                </h2>
                <p>
                  This Cookie Policy explains how Atlas Softech Limited ("we," "our," or "us") uses cookies and similar tracking technologies on our website. This policy is designed to comply with the <strong>Personal Data Protection Act of Tanzania</strong>, the <strong>Electronic and Postal Communications Act (EPOCA)</strong>, and international best practices for data protection.
                </p>
                <p>
                  By continuing to use our website, you consent to the use of cookies as described in this policy. You may manage your cookie preferences as outlined below.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  2. What Are Cookies?
                </h2>
                <p>
                  Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work efficiently and provide information to website owners.
                </p>
                <p>
                  Cookies serve various purposes, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Remembering your preferences and settings</li>
                  <li>Keeping you signed in to your account</li>
                  <li>Understanding how you use our website</li>
                  <li>Improving your user experience</li>
                  <li>Providing relevant content and features</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  3. Types of Cookies We Use
                </h2>
                
                <h3 className={`text-xl font-semibold mt-4 mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  3.1 Strictly Necessary Cookies
                </h3>
                <p>
                  These cookies are essential for the operation of our website. They enable basic functions such as page navigation, secure access to certain areas, and form submissions. Without these cookies, the website cannot function properly.
                </p>
                <div className={`p-4 rounded-lg mt-2 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-100'}`}>
                  <p><strong>Examples:</strong></p>
                  <ul className="list-disc pl-6 mt-2">
                    <li>Session management cookies</li>
                    <li>Authentication cookies</li>
                    <li>Security cookies</li>
                  </ul>
                </div>

                <h3 className={`text-xl font-semibold mt-6 mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  3.2 Functional Cookies
                </h3>
                <p>
                  These cookies enable enhanced functionality and personalization, such as remembering your preferences (e.g., language or theme settings). If you disable these cookies, some features may not work properly.
                </p>
                <div className={`p-4 rounded-lg mt-2 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-100'}`}>
                  <p><strong>Examples:</strong></p>
                  <ul className="list-disc pl-6 mt-2">
                    <li>Theme preference (dark/light mode)</li>
                    <li>Language preferences</li>
                    <li>User interface customizations</li>
                  </ul>
                </div>

                <h3 className={`text-xl font-semibold mt-6 mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  3.3 Analytics Cookies
                </h3>
                <p>
                  These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This data helps us improve our website's performance and user experience.
                </p>
                <div className={`p-4 rounded-lg mt-2 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-100'}`}>
                  <p><strong>Examples:</strong></p>
                  <ul className="list-disc pl-6 mt-2">
                    <li>Pages visited and time spent</li>
                    <li>Navigation patterns</li>
                    <li>Error messages encountered</li>
                  </ul>
                </div>

                <h3 className={`text-xl font-semibold mt-6 mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  3.4 Marketing Cookies
                </h3>
                <p>
                  These cookies may be used to track visitors across websites and display relevant advertisements. We currently do not use third-party marketing cookies. If this changes, we will update this policy and seek your consent where required.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  4. Cookie Duration
                </h2>
                <p>
                  Cookies can be categorized by their duration:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Session Cookies:</strong> These are temporary cookies that are deleted when you close your browser. They are used to maintain your session while you navigate our website.</li>
                  <li><strong>Persistent Cookies:</strong> These cookies remain on your device for a specified period or until you delete them. They are used to remember your preferences across multiple sessions.</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  5. Third-Party Cookies
                </h2>
                <p>
                  In some cases, we may use cookies from trusted third-party services. These third parties have their own privacy and cookie policies, and we encourage you to review them:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Analytics providers (for website performance analysis)</li>
                  <li>Security service providers (for protection against threats)</li>
                  <li>Content delivery networks (for efficient content delivery)</li>
                </ul>
                <p className="mt-4">
                  We ensure that any third-party cookies used comply with Tanzanian data protection requirements and are subject to appropriate data processing agreements.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  6. Your Rights and Choices
                </h2>
                <p>
                  Under Tanzanian data protection law, you have the right to control how cookies are used on your device. You can manage your cookie preferences in the following ways:
                </p>
                
                <h3 className={`text-xl font-semibold mt-4 mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  6.1 Browser Settings
                </h3>
                <p>
                  Most web browsers allow you to control cookies through their settings. You can:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>View cookies stored on your device</li>
                  <li>Delete existing cookies</li>
                  <li>Block all or certain cookies</li>
                  <li>Set preferences for specific websites</li>
                </ul>
                <p className="mt-2">
                  Please note that blocking certain cookies may affect the functionality of our website.
                </p>

                <h3 className={`text-xl font-semibold mt-4 mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  6.2 Browser-Specific Instructions
                </h3>
                <p>
                  For guidance on managing cookies in your browser, please visit:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Google Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                  <li><strong>Mozilla Firefox:</strong> Options → Privacy & Security → Cookies</li>
                  <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                  <li><strong>Microsoft Edge:</strong> Settings → Privacy, Search, and Services → Cookies</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  7. Legal Basis for Using Cookies
                </h2>
                <p>
                  In accordance with Tanzanian data protection law, we use cookies based on the following legal grounds:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Consent:</strong> For non-essential cookies, we obtain your consent before placing them on your device</li>
                  <li><strong>Legitimate Interest:</strong> For strictly necessary cookies that are essential for the operation of our website</li>
                  <li><strong>Contractual Necessity:</strong> Where cookies are required to provide services you have requested</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  8. Data Protection and Security
                </h2>
                <p>
                  We implement appropriate technical and organizational measures to protect the data collected through cookies. This includes:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encryption of data in transit</li>
                  <li>Secure storage of cookie-related data</li>
                  <li>Regular review of cookie usage and data collection practices</li>
                  <li>Limiting access to cookie data to authorized personnel only</li>
                </ul>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  9. Updates to This Policy
                </h2>
                <p>
                  We may update this Cookie Policy from time to time to reflect changes in our practices, technology, or legal requirements. Any changes will be posted on this page with an updated effective date.
                </p>
                <p>
                  We encourage you to review this policy periodically to stay informed about how we use cookies.
                </p>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  10. Contact Us
                </h2>
                <p>
                  If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
                </p>
                <div className={`p-4 rounded-lg mt-4 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-100'}`}>
                  <p><strong>Atlas Softech Limited</strong></p>
                  <p>Data Protection Officer</p>
                  <p>Dar Es Salaam, United Republic of Tanzania</p>
                  <p>Email: contact@atlassoftech.com</p>
                  <p>Phone: +255 799 227 202</p>
                </div>
              </section>

              <section>
                <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  11. Related Policies
                </h2>
                <p>
                  For more information about how we handle your personal data, please refer to our:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><Link to="/privacy-policy" className="text-[#00C8EB] hover:underline">Privacy Policy</Link></li>
                  <li><Link to="/terms-of-service" className="text-[#00C8EB] hover:underline">Terms of Service</Link></li>
                </ul>
              </section>

            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
